import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import Cookies from "js-cookie";

// Flag URLs or local imports
import arFlag from '../../assets/icons/languageIconsSvg/Lng-Ar.svg'; // 🇦 Arabic flag
import enFlag from '../../assets/icons/languageIconsSvg/Lng-En.svg'; // 🇺🇸 English flag

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', label: 'English', flag: enFlag },
    { code: 'ar', label: 'العربية', flag: arFlag }
  ];

  const currentSelected = languages.find(lang => lang.code === currentLang) || languages[0];

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    document.documentElement.setAttribute('dir', newLang === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.lang = newLang;
    Cookies.set("selectedLanguage", newLang, { expires: 365 }); 
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className='flex justify-center'>
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Selector Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lang-btn flex items-center gap-2 px-4 py-2 bg-white/95 hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-full shadow-md shadow-slate-100 dark:shadow-none hover:shadow-lg transition-all duration-300 font-extrabold text-sm text-slate-700 dark:text-slate-300 focus:outline-none"
        style={{ direction: currentLang === 'ar' ? 'rtl' : 'ltr' }}
      >
        <img
          src={currentSelected.flag}
          alt={currentSelected.label}
          className="w-6 h-4 object-contain rounded-sm"
        />
        <span className="hidden sm:inline font-black text-slate-800 dark:text-slate-200">
          {currentSelected.code === 'ar' ? 'العربية' : 'English'}
        </span>
        <ChevronDown 
          className={`w-4 h-4 text-slate-400 dark:text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600 dark:text-blue-400' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className={`absolute ${currentLang === 'ar' ? 'left-0' : 'right-0'} mt-2 w-40 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-2xl dark:shadow-slate-950/80 p-2 z-50 transform origin-top-right transition-all duration-200`}
          style={{ direction: currentLang === 'ar' ? 'rtl' : 'ltr' }}
        >
          <div className="flex flex-col gap-1">
            {languages.map((lang) => {
              const isSelected = lang.code === currentLang;
              return (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`lang-btn w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-black transition-all ${
                    isSelected 
                      ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400' 
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <img
                      src={lang.flag}
                      alt={lang.label}
                      className="w-6 h-4 object-contain rounded-sm"
                    />
                    <span>{lang.label}</span>
                  </div>
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default LanguageSwitcher;
